import React from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNoizaMetrics } from '../../lib/noiza-metrics-hooks';
import { formatCurrency } from '../../lib/utils';
import { TrendingUpIcon, ShoppingCartIcon, ReceiptIcon, StoreIcon, UsersIcon, UserCheckIcon, ArrowLeftIcon, ActivityIcon, RefreshCwIcon, Loader2Icon } from 'lucide-react';
// ─── Helpers ───────────────────────────────────────────────
function formatCompactCurrency(value: number): string {
  if (value >= 1000000) return `₡${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₡${(value / 1000).toFixed(0)}k`;
  return formatCurrency(value);
}
function formatDayLabel(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${d} ${months[m - 1]}`;
  } catch {
    return dateStr;
  }
}
function normalizeChannel(ch: string): string {
  const lower = (ch || '').toLowerCase();
  if (lower.includes('qr') || lower.includes('self')) return 'QR';
  if (lower.includes('waiter') || lower.includes('mesero')) return 'Mesero';
  if (lower.includes('web') || lower.includes('online')) return 'Web';
  if (lower.includes('caja') || lower.includes('cashier')) return 'Caja';
  return ch || 'Otro';
}
const CHANNEL_COLORS: Record<string, string> = {
  QR: '#10b981',
  Mesero: '#6366f1',
  Web: '#f59e0b',
  Caja: '#8b5cf6',
  Otro: '#6b7280'
};
// ─── Dark Tooltip ──────────────────────────────────────────
function DarkTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl text-sm" data-id="element-1499">
      <p className="font-medium text-white/60 mb-1.5 text-xs uppercase tracking-wider" data-id="element-1500">
        {label}
      </p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-1501">
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color
      }} data-id="element-1502" />
          <span className="text-white/50" data-id="element-1503">{entry.name}:</span>
          <span className="font-semibold text-white" data-id="element-1504">
            {isCurrency ? formatCurrency(entry.value) : entry.value.toLocaleString()}
          </span>
        </p>)}
    </div>;
}
// ─── KPI Card ──────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: string;
}
function KpiCard({
  label,
  value,
  subtitle,
  icon,
  accentColor
}: KpiCardProps) {
  return <div className="relative overflow-hidden rounded-2xl bg-[#12121a] border border-white/[0.06] p-5 group" data-id="element-1505">
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-[0.07] -translate-y-8 translate-x-8`} style={{
      backgroundColor: accentColor
    }} data-id="element-1506" />
      <div className="relative" data-id="element-1507">
        <div className="flex items-center justify-between mb-4" data-id="element-1508">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-1509">
            {icon}
          </div>
        </div>
        <p className="text-[13px] font-medium text-white/40 uppercase tracking-wider" data-id="element-1510">
          {label}
        </p>
        <p className="text-3xl font-bold text-white mt-1 tracking-tight" data-id="element-1511">
          {value}
        </p>
        {subtitle && <p className="text-xs text-white/30 mt-1" data-id="element-1512">{subtitle}</p>}
      </div>
    </div>;
}
// ─── Main Page ─────────────────────────────────────────────
export function NoizaMetricsPage() {
  const {
    data,
    loading,
    error,
    refetch
  } = useNoizaMetrics();
  const dailyChartData = data.gmvDaily.map(d => ({
    ...d,
    label: formatDayLabel(d.day)
  }));
  const channelChartData = data.channelDistribution.map(d => ({
    name: normalizeChannel(d.channel),
    value: d.orders,
    pct: d.pct,
    color: CHANNEL_COLORS[normalizeChannel(d.channel)] || '#6b7280'
  })).filter(d => d.value > 0);
  const totalChannelOrders = channelChartData.reduce((sum, d) => sum + d.value, 0);
  return <div className="min-h-screen bg-[#0a0a0f] text-white" data-id="element-1513">
      {/* ─── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-1514">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between" data-id="element-1515">
          <div className="flex items-center gap-4" data-id="element-1516">
            <Link to="/" className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.1] transition-colors" data-id="element-1517">
              <ArrowLeftIcon className="w-4 h-4 text-white/60" data-id="element-1518" />
            </Link>
            <div data-id="element-1519">
              <div className="flex items-center gap-2.5" data-id="element-1520">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20" data-id="element-1521">
                  <ActivityIcon className="w-4 h-4 text-white" data-id="element-1522" />
                </div>
                <h1 className="text-lg font-bold tracking-tight" data-id="element-1523">NOIZA</h1>
                <span className="text-xs font-medium text-white/30 bg-white/[0.06] px-2 py-0.5 rounded-md" data-id="element-1524">
                  Platform Metrics
                </span>
              </div>
            </div>
          </div>
          <button onClick={refetch} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] transition-colors text-sm text-white/60 hover:text-white/80 disabled:opacity-50" data-id="element-1525">
            {loading ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-1526" /> : <RefreshCwIcon className="w-4 h-4" data-id="element-1527" />}
            <span className="hidden sm:inline" data-id="element-1528">Actualizar</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-1529">
        {/* ─── Error State ────────────────────────────── */}
        {error && <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400" data-id="element-1530">
            {error}
          </div>}

        {/* ─── Loading State ──────────────────────────── */}
        {loading ? <div className="space-y-8" data-id="element-1531">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-1532">
              {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1533" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1534">
              <div className="lg:col-span-2 h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1535" />
              <div className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1536" />
            </div>
            <div className="h-64 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1537" />
          </div> : <>
            {/* ─── KPI Cards ──────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-1538">
              <KpiCard label="GMV Total" value={formatCompactCurrency(data.gmv.total_gmv)} subtitle="Gross Merchandise Value" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-1540" />} accentColor="#10b981" data-id="element-1539" />
              <KpiCard label="Órdenes" value={data.gmv.total_orders.toLocaleString()} subtitle="Total procesadas" icon={<ShoppingCartIcon className="w-5 h-5 text-blue-400" data-id="element-1542" />} accentColor="#3b82f6" data-id="element-1541" />
              <KpiCard label="Ticket Promedio" value={formatCurrency(data.gmv.avg_ticket)} subtitle="Por orden" icon={<ReceiptIcon className="w-5 h-5 text-amber-400" data-id="element-1544" />} accentColor="#f59e0b" data-id="element-1543" />
              <KpiCard label="Restaurantes" value={String(data.activeRestaurants)} subtitle="Activos en plataforma" icon={<StoreIcon className="w-5 h-5 text-purple-400" data-id="element-1546" />} accentColor="#8b5cf6" data-id="element-1545" />
              <KpiCard label="Clientes Únicos" value={data.uniqueCustomers.toLocaleString()} subtitle="Compradores distintos" icon={<UsersIcon className="w-5 h-5 text-cyan-400" data-id="element-1548" />} accentColor="#06b6d4" data-id="element-1547" />
              <KpiCard label="Recurrentes" value={data.repeatCustomers.repeat_count.toLocaleString()} subtitle={data.repeatCustomers.repeat_pct > 0 ? `${Math.round(data.repeatCustomers.repeat_pct)}% del total` : 'Clientes que regresan'} icon={<UserCheckIcon className="w-5 h-5 text-pink-400" data-id="element-1550" />} accentColor="#ec4899" data-id="element-1549" />
            </div>

            {/* ─── Charts Row ─────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1551">
              {/* GMV Daily Chart */}
              <div className="lg:col-span-2 rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-1552">
                <div className="flex items-center justify-between mb-6" data-id="element-1553">
                  <div data-id="element-1554">
                    <h2 className="text-sm font-semibold text-white/80" data-id="element-1555">
                      GMV Diario
                    </h2>
                    <p className="text-xs text-white/30 mt-0.5" data-id="element-1556">
                      Ingresos brutos por día
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-white/30" data-id="element-1557">
                    <span className="flex items-center gap-1.5" data-id="element-1558">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-1559" />
                      GMV
                    </span>
                  </div>
                </div>
                {dailyChartData.length === 0 ? <div className="h-64 flex items-center justify-center text-white/20 text-sm" data-id="element-1560">
                    Sin datos disponibles
                  </div> : <div className="h-64" data-id="element-1561">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1562">
                      <AreaChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-1563">
                        <defs data-id="element-1564">
                          <linearGradient id="noizaGmvGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-1565">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} data-id="element-1566" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-1567" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-1568" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval={dailyChartData.length > 15 ? Math.floor(dailyChartData.length / 8) : 0} data-id="element-1569" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={v => formatCompactCurrency(v)} data-id="element-1570" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-1572" />} data-id="element-1571" />
                        <Area type="monotone" dataKey="gmv" name="GMV" stroke="#10b981" strokeWidth={2} fill="url(#noizaGmvGrad)" dot={false} activeDot={{
                    r: 4,
                    fill: '#10b981',
                    stroke: '#0a0a0f',
                    strokeWidth: 2
                  }} data-id="element-1573" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Channel Distribution */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-1574">
                <div className="mb-4" data-id="element-1575">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-1576">
                    Canales
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-1577">
                    Distribución de pedidos
                  </p>
                </div>
                {channelChartData.length === 0 ? <div className="h-48 flex items-center justify-center text-white/20 text-sm" data-id="element-1578">
                    Sin datos
                  </div> : <>
                    <div className="h-48" data-id="element-1579">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-1580">
                        <PieChart data-id="element-1581">
                          <Pie data={channelChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-1582">
                            {channelChartData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-1583" />)}
                          </Pie>
                          <Tooltip content={({
                      active,
                      payload
                    }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const pct = totalChannelOrders > 0 ? Math.round(d.value / totalChannelOrders * 100) : 0;
                      return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-3 py-2 rounded-xl shadow-2xl text-sm" data-id="element-1585">
                                  <p className="font-medium" data-id="element-1586">{d.name}</p>
                                  <p className="text-white/50" data-id="element-1587">
                                    {d.value.toLocaleString()} pedidos ({pct}%)
                                  </p>
                                </div>;
                    }} data-id="element-1584" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2" data-id="element-1588">
                      {channelChartData.map(entry => {
                  const pct = totalChannelOrders > 0 ? Math.round(entry.value / totalChannelOrders * 100) : 0;
                  return <div key={entry.name} className="flex items-center justify-between" data-id="element-1589">
                            <div className="flex items-center gap-2" data-id="element-1590">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                        backgroundColor: entry.color
                      }} data-id="element-1591" />
                              <span className="text-sm text-white/60" data-id="element-1592">
                                {entry.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-1593">
                              <span className="text-sm font-semibold text-white/90" data-id="element-1594">
                                {entry.value.toLocaleString()}
                              </span>
                              <span className="text-xs text-white/30 w-10 text-right" data-id="element-1595">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                })}
                    </div>
                  </>}
              </div>
            </div>

            {/* ─── Orders per Day Chart ───────────────── */}
            <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-1596">
              <div className="flex items-center justify-between mb-6" data-id="element-1597">
                <div data-id="element-1598">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-1599">
                    Órdenes Diarias
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-1600">
                    Volumen de pedidos por día
                  </p>
                </div>
              </div>
              {dailyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-1601">
                  Sin datos disponibles
                </div> : <div className="h-56" data-id="element-1602">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1603">
                    <BarChart data={dailyChartData} margin={{
                top: 4,
                right: 4,
                left: 0,
                bottom: 0
              }} data-id="element-1604">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-1605" />
                      <XAxis dataKey="label" tick={{
                  fontSize: 11,
                  fill: 'rgba(255,255,255,0.25)'
                }} tickLine={false} axisLine={{
                  stroke: 'rgba(255,255,255,0.06)'
                }} interval={dailyChartData.length > 15 ? Math.floor(dailyChartData.length / 8) : 0} data-id="element-1606" />
                      <YAxis tick={{
                  fontSize: 11,
                  fill: 'rgba(255,255,255,0.25)'
                }} tickLine={false} axisLine={false} data-id="element-1607" />
                      <Tooltip content={<DarkTooltip isCurrency={false} data-id="element-1609" />} data-id="element-1608" />
                      <Bar dataKey="orders" name="Órdenes" fill="#6366f1" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-1610" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>}
            </div>

            {/* ─── Top Restaurants Table ───────────────── */}
            <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden" data-id="element-1611">
              <div className="p-5 border-b border-white/[0.06]" data-id="element-1612">
                <h2 className="text-sm font-semibold text-white/80" data-id="element-1613">
                  Top Restaurantes por GMV
                </h2>
                <p className="text-xs text-white/30 mt-0.5" data-id="element-1614">
                  Rendimiento por establecimiento
                </p>
              </div>
              {data.gmvByRestaurant.length === 0 ? <div className="p-8 text-center text-white/20 text-sm" data-id="element-1615">
                  Sin datos de restaurantes
                </div> : <div className="overflow-x-auto" data-id="element-1616">
                  <table className="w-full text-sm" data-id="element-1617">
                    <thead data-id="element-1618">
                      <tr className="text-xs text-white/30 uppercase tracking-wider" data-id="element-1619">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-1620">#</th>
                        <th className="text-left px-5 py-3 font-medium" data-id="element-1621">
                          Restaurante
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1622">
                          GMV
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1623">
                          Órdenes
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1624">
                          Ticket Prom.
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1625">
                          % del Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1626">
                      {data.gmvByRestaurant.map((r, i) => {
                  const gmvPct = data.gmv.total_gmv > 0 ? (r.gmv / data.gmv.total_gmv * 100).toFixed(1) : '0';
                  return <tr key={r.restaurant_id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-1627">
                            <td className="px-5 py-3.5" data-id="element-1628">
                              <span className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-white/[0.08] text-white/50' : i === 2 ? 'bg-orange-500/15 text-orange-400/80' : 'bg-transparent text-white/20'}`} data-id="element-1629">
                                {i + 1}
                              </span>
                            </td>
                            <td className="px-5 py-3.5" data-id="element-1630">
                              <div data-id="element-1631">
                                <p className="font-medium text-white/90" data-id="element-1632">
                                  {r.restaurant_name}
                                </p>
                                {r.slug && <p className="text-xs text-white/25 mt-0.5" data-id="element-1633">
                                    {r.slug}
                                  </p>}
                              </div>
                            </td>
                            <td className="px-5 py-3.5 text-right font-semibold text-emerald-400" data-id="element-1634">
                              {formatCurrency(r.gmv)}
                            </td>
                            <td className="px-5 py-3.5 text-right text-white/60" data-id="element-1635">
                              {r.orders.toLocaleString()}
                            </td>
                            <td className="px-5 py-3.5 text-right text-white/60" data-id="element-1636">
                              {formatCurrency(r.avg_ticket)}
                            </td>
                            <td className="px-5 py-3.5 text-right" data-id="element-1637">
                              <div className="flex items-center justify-end gap-2" data-id="element-1638">
                                <div className="w-16 h-1.5 rounded-full bg-white/[0.06] overflow-hidden" data-id="element-1639">
                                  <div className="h-full rounded-full bg-emerald-500/60" style={{
                            width: `${Math.min(parseFloat(gmvPct), 100)}%`
                          }} data-id="element-1640" />
                                </div>
                                <span className="text-xs text-white/40 w-10 text-right" data-id="element-1641">
                                  {gmvPct}%
                                </span>
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>}
            </div>

            {/* ─── Footer ─────────────────────────────── */}
            <div className="text-center py-4" data-id="element-1642">
              <p className="text-xs text-white/15" data-id="element-1643">
                NOIZA Platform Analytics · Datos actualizados cada minuto
              </p>
            </div>
          </>}
      </main>
    </div>;
}